import { StrictMode, useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom/client';
import { installStaleChunkGuard, clearStaleChunkMark } from '@/shared/lib/staleChunkReload';
import { ConfigProvider, App as AntApp } from 'antd';
import ruRU from 'antd/locale/ru_RU';
import enUS from 'antd/locale/en_US';
import { useTranslation } from 'react-i18next';
import { QueryClientProvider, useQuery } from '@tanstack/react-query';
import { appQueryClient } from '@shared/lib/appQueryClient';
import { useUIStore } from '@shared/store/uiStore';
import { useAuthStore } from '@features/auth/store/authStore';
import App from './app/App';
import { WebSocketNotificationInit } from '@shared/components/WebSocketNotificationInit';
import './index.css';
import './i18n/config';
import { initWebVitals } from './perf/webVitals';
import { buildThemeConfig } from '@theme/config';
import { userSettingsApi } from '@features/settings/api/userSettingsApi';
import { useDeviceInterfaceHistoryStore } from '@features/devices/store/deviceInterfaceHistoryStore';
import { SplashScreen } from '@shared/components/SplashScreen/SplashScreen';

// Минимальная длительность брендового сплэша после авторизации (мс).
// Чтобы момент «Крапива · V5» был виден, а не мелькал. Тюнится тут.
const SPLASH_MIN_MS = 2400;

// Компонент-обертка для темы
function AppWithTheme() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const {
    theme: currentTheme,
    themeTokens,
    themeComponents,
    setTheme,
    setThemeTokens,
    setThemeComponents,
  } = useUIStore((state) => ({
    theme: state.theme,
    themeTokens: state.themeTokens,
    themeComponents: state.themeComponents,
    setTheme: state.setTheme,
    setThemeTokens: state.setThemeTokens,
    setThemeComponents: state.setThemeComponents,
  }));
  const { i18n } = useTranslation();

  const locale = useMemo(() => (i18n.language === 'ru' ? ruRU : enUS), [i18n.language]);
  const themeConfig = useMemo(
    () => buildThemeConfig(currentTheme, { tokens: themeTokens ?? undefined, components: themeComponents ?? undefined }),
    [currentTheme, themeComponents, themeTokens],
  );

  // ✅ ОПТИМИЗАЦИЯ: Загружаем настройки пользователя через React Query (вместо искусственной задержки)
  // Используем короткий staleTime для быстрой загрузки, но не блокируем рендер
  const { isLoading: isLoadingSettings } = useQuery({
    queryKey: ['userSettings', 'theme'],
    queryFn: async () => {
      try {
        const settings = await userSettingsApi.getSettings();
        if (settings.theme === 'light' || settings.theme === 'dark') {
          setTheme(settings.theme);
        }
        const overrides = settings.widget_settings?.theme_overrides;
        if (overrides?.tokens) {
          setThemeTokens(overrides.tokens);
        }
        if (overrides?.components) {
          setThemeComponents(overrides.components);
        }
        
        // ✅ АРХИТЕКТУРА: Загружаем закрепленные интерфейсы в стор
        if (settings.device_preferences?.pinned_interfaces) {
          const pins: Record<number, number[]> = {};
          Object.entries(settings.device_preferences.pinned_interfaces).forEach(([id, indexes]) => {
            pins[parseInt(id, 10)] = indexes;
          });
          useDeviceInterfaceHistoryStore.getState().setPinnedInterfaces(pins, settings.device_preferences.version);
        }
        
        return settings;
      } catch (error) {
        console.warn('Не удалось загрузить настройки темы пользователя', error);
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error: any) => error?.response?.status === 401 ? false : failureCount < 1,
    enabled: isAuthenticated,
    refetchOnMount: false,
  });

  // ✅ Брендовый сплэш-момент после авторизации.
  // Настройки/бандл грузятся быстро → без минимальной длительности сплэш мелькал доли секунды.
  // Держим сплэш минимум SPLASH_MIN_MS с момента, когда юзер стал авторизован
  // (покрывает и переход login→app, и перезагрузку под логином).
  const [minSplashElapsed, setMinSplashElapsed] = useState(false);
  useEffect(() => {
    if (!isAuthenticated) {
      setMinSplashElapsed(false);
      return;
    }
    setMinSplashElapsed(false);
    const id = window.setTimeout(() => setMinSplashElapsed(true), SPLASH_MIN_MS);
    return () => window.clearTimeout(id);
  }, [isAuthenticated]);

  // Сплэш показываем пока грузятся настройки ИЛИ не истёк минимальный показ.
  const isInitializing = isAuthenticated && (isLoadingSettings || !minSplashElapsed);

  // Пробрасываем основные цветовые токены в CSS переменные (для кастомных стилей сайдбара/логотипа)
  useEffect(() => {
    const toRgba = (hex: string | undefined, alpha: number) => {
      if (!hex || !hex.startsWith('#')) return hex ?? '';
      const normalized = hex.length === 4
        ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`
        : hex;
      const r = parseInt(normalized.slice(1, 3), 16);
      const g = parseInt(normalized.slice(3, 5), 16);
      const b = parseInt(normalized.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    const root = document.documentElement;
    const primary = themeConfig.token?.colorPrimary ?? '#1677ff';
    const primaryStrong = themeConfig.token?.colorPrimaryHover ?? themeConfig.token?.colorPrimaryActive ?? primary;

    root.style.setProperty('--color-primary', primary);
    root.style.setProperty('--color-primary-strong', primaryStrong);
    root.style.setProperty('--color-primary-shadow', toRgba(primary, 0.3));
    root.style.setProperty('--color-primary-hover-bg', toRgba(primary, 0.08));
    root.style.setProperty('--color-primary-popup', toRgba(primary, 0.12));
    root.style.setProperty('--color-primary-popup-strong', toRgba(primary, 0.2));
    root.style.setProperty('--color-primary-hover', toRgba(primary, 0.15));

    // Тени для кастомных блоков/дешборда
    const shadow = themeConfig.token?.boxShadow ?? '';
    const shadowSecondary = themeConfig.token?.boxShadowSecondary ?? '';
    root.style.setProperty('--box-shadow-primary', shadow);
    root.style.setProperty('--box-shadow-secondary', shadowSecondary);
  }, [themeConfig]);

  // Применить класс dark к HTML элементу для Tailwind
  useEffect(() => {
    const root = document.documentElement;
    if (currentTheme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [currentTheme]);

  // Показываем экран загрузки во время инициализации
  if (isInitializing) {
    return <SplashScreen />;
  }

  return (
    <ConfigProvider
      locale={locale}
      theme={themeConfig}
    >
      <AntApp>
        <WebSocketNotificationInit />
        <App />
      </AntApp>
    </ConfigProvider>
  );
}

// Вкладка, пережившая выкат новой версии, падает белым экраном на первой же ленивой
// загрузке: её index.html держит хеши чанков, которых на сервере уже нет. Слушатели
// ставятся ДО первого рендера — ошибка может прийти на самом первом переходе.
// Метка снимается здесь же: раз мы дошли до загрузки приложения, прошлая перезагрузка
// удалась, и следующий выкат должен лечиться тем же способом.
clearStaleChunkMark();
installStaleChunkGuard();

// ✅ ОПТИМИЗАЦИЯ: Переиспользование React root для избежания множественных рендеров при HMR
// Сохраняем root в глобальной переменной, чтобы не создавать его заново при Hot Module Replacement
const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element not found');
}

// Проверяем, есть ли уже созданный root (для HMR в dev режиме)
let root: ReturnType<typeof ReactDOM.createRoot>;


// В dev режиме при HMR может быть уже созданный root
if (import.meta.env.DEV && (window as any).__REACT_ROOT__) {
  root = (window as any).__REACT_ROOT__;
  // Просто обновляем содержимое существующего root
  root.render(
    <StrictMode>
      <QueryClientProvider client={appQueryClient}>
        <AppWithTheme />
      </QueryClientProvider>
    </StrictMode>
  );
} else {
  // Создаем новый root
  root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      <QueryClientProvider client={appQueryClient}>
        <AppWithTheme />
      </QueryClientProvider>
    </StrictMode>
  );
  // Сохраняем root в глобальной переменной для HMR (только в dev)
  if (import.meta.env.DEV) {
    (window as any).__REACT_ROOT__ = root;
  }
}

export { AppWithTheme };

// Инициализировать сбор метрик производительности
initWebVitals();

